import { browserTracingIntegration, init, captureException } from '@sentry/vue'
import type { EventHint, Event, Breadcrumb } from '@sentry/vue'
// @ts-ignore
import type { Options, Vue } from '@sentry/vue/types/types'
import packageJson from '../../../../package.json'
import type { Router } from 'vue-router'

type ErrorMessage = { message: string | string[] }

const isError = (error: EventHint['originalException']): error is ErrorMessage =>
  (error as ErrorMessage).message !== undefined

const productionHost = 'qic.online'
const environment = location.host === productionHost ? 'production' : 'test'
const release = packageJson.version

const ignoreList = [
  'Invalid time value', // ios safari false alarm
  'network_error', // TBA
  'Unexpected token', // TBA
  'roistat', // analytics
  'Move target is disabled', // ios safari false alarm
  'no_name_error', // just duplicate
  'chunk', // cache problem
  'Failed to fetch', // requests in analytics
  'getBattery', // requests in analytics
  'Navigation', // router
  'Load failed', // requests in analytics
  'prototype', // requests in analytics
  'Unexpected', // user's network
  '.replaceAll', // 3rd party lib error
  'frame', // requests in analytics
  'Illegal invocation', // requests in analytics
  'Page already has an active payment session', // apple pay
  'An attempt was made to break through the security policy of the user agent', // clickcease
]

const ignoreCrumbList = ['webvisor', 'analytics', 'roistat']
const allowUrls = [
  'https://qic.online',
  'https://qicnew.nado.com',
  'https://lp.qic-insured.com',
  'https://pp.qic-insured.com',
]

const ignoreTransactions = [
  'https://px.ads.linkedin.com',
  'https://monitor.clickcease.com',
  /https:\/\/mc\.yandex\.(ru|com)/,
  'https://tr6.snapchat.com',
  'https://translate-pa.googleapis.com',
  'https://api.amplitude.com',
]

export const SentryLib = {
  init(app: Vue, router: Router) {
    const config: Partial<Omit<Options, 'tracingOptions'>> = {
      app,
      dsn: import.meta.env.VUE_APP_SENTRY_DSN,
      environment,
      allowUrls,
      ignoreTransactions,
      release,
      beforeSend: (body: Event, hint: EventHint) => {
        const error = hint.originalException
        let inIgnore

        if (isError(error)) {
          inIgnore = ignoreList.some((sentence) => error.message.indexOf(sentence) !== -1)
        }

        return inIgnore ? null : body
      },
      beforeBreadcrumb(breadcrumb: Breadcrumb) {
        return ignoreCrumbList.some((sentence) => breadcrumb.message?.indexOf(sentence) !== -1)
          ? null
          : breadcrumb
      },
    }

    if (environment === 'production') {
      config.tracesSampleRate = 0.5
      config.integrations = [browserTracingIntegration({ router })]
    }

    init(config)
  },
  pushError(message: string) {
    const error = new Error(message)
    captureException(error)
  },
}
